import React, { useEffect, useState } from 'react';
import { Volume2, VolumeX, Play } from 'lucide-react';
import {
    isSoundEnabled,
    setSoundEnabled,
    getSoundVolume,
    setSoundVolume,
    hasSoundConsent,
    grantSoundConsent,
    SOUND_TRIGGERS,
} from '../lib/notificationSounds';

// Per-device sound preferences. Each browser/tablet maintains its own
// settings — useful when one device is at the bar (loud) and another at
// the kitchen pass (quiet). Nothing is sent to the server.
export function NotificationSoundsSettings() {
    const [enabled, setEnabled] = useState(isSoundEnabled());
    const [volume, setVolume] = useState(getSoundVolume());
    const [consent, setConsent] = useState(hasSoundConsent());

    useEffect(() => { setSoundEnabled(enabled); }, [enabled]);
    useEffect(() => { setSoundVolume(volume); }, [volume]);

    const ensureConsent = () => {
        if (!consent) {
            grantSoundConsent();
            setConsent(true);
        }
    };

    const testSound = (key) => {
        ensureConsent();
        if (!enabled) setEnabled(true);
        // The trigger functions throttle each sound to once per 1.5s,
        // so we force-play here to make the test buttons always responsive.
        SOUND_TRIGGERS[key]({ force: true });
    };

    return (
        <div className="panel" style={{ padding: '1.2rem' }}>
            <h3 style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                {enabled ? <Volume2 size={20} /> : <VolumeX size={20} />}
                Sunete notificare
            </h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginTop: 4 }}>
                Setări per dispozitiv (browser). Sunetele se redau direct pe ecranul curent — fiecare
                tabletă sau telefon poate avea propria configurație de volum.
            </p>

            <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0.6rem 0', cursor: 'pointer' }}>
                <input
                    type="checkbox"
                    checked={enabled}
                    onChange={(e) => { ensureConsent(); setEnabled(e.target.checked); }}
                />
                <span><strong>Activează sunete</strong></span>
            </label>

            <div style={{ marginTop: '0.4rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.9rem' }}>
                    <span style={{ minWidth: 60, color: 'var(--text-muted)' }}>Volum</span>
                    <input
                        type="range"
                        min={0}
                        max={1}
                        step={0.05}
                        value={volume}
                        onChange={(e) => setVolume(parseFloat(e.target.value))}
                        disabled={!enabled}
                        style={{ flex: 1, maxWidth: 240 }}
                    />
                    <span style={{ minWidth: 40, textAlign: 'right', color: 'var(--text-muted)' }}>
                        {Math.round(volume * 100)}%
                    </span>
                </label>
            </div>

            <div style={{ marginTop: '1.2rem', borderTop: '1px solid var(--border)', paddingTop: '1rem' }}>
                <div className="kicker" style={{ marginBottom: 10 }}>Testare</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {[
                        { key: 'order', label: 'Comandă nouă', hint: 'Două note ascendente — ca un POS clasic' },
                        { key: 'waiter', label: 'Chemare ospătar', hint: 'Clopoțel de recepție, atrage atenția' },
                        { key: 'bill', label: 'Cerere notă', hint: 'Sunet calm — clientul vrea să plătească' },
                    ].map((s) => (
                        <div key={s.key} className="panel" style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            gap: 12, padding: '0.6rem 0.8rem',
                        }}>
                            <div>
                                <div style={{ fontWeight: 600 }}>{s.label}</div>
                                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{s.hint}</div>
                            </div>
                            <button
                                type="button"
                                className="btn btn-sm"
                                onClick={() => testSound(s.key)}
                                disabled={!enabled}
                                title="Redă sunetul"
                            >
                                <Play size={14} style={{ marginRight: 6 }} />
                                Test
                            </button>
                        </div>
                    ))}
                </div>
            </div>

            <p style={{ fontSize: '0.78rem', color: 'var(--text-faint)', marginTop: '1rem', lineHeight: 1.5 }}>
                <strong>Notă:</strong> browser-ele blochează sunetul automat până apeși măcar o dată
                pe pagină. Dacă tableta stă deschisă peste noapte și se relogheaază singură,
                primul sunet poate să nu meargă — fă un click oriunde și reia.
            </p>
        </div>
    );
}
